/**
 * Lịch sử tài sản ròng: mỗi tháng một tài liệu `healthMetrics/networth-yyyy-mm` (lần mở app gần nhất trong tháng ghi đè).
 * Đọc một lần mỗi phiên (≤ vài chục tài liệu); ghi tối đa một lần mỗi ngày, bỏ qua im lặng nếu lỗi/hết hạn mức.
 */
import { useEffect, useMemo, useState } from 'react';
import { getAuth } from 'firebase/auth';
import { getFirestore, collection, getDocs, setDoc, doc, Timestamp } from 'firebase/firestore';
import { APP_ID } from '../../../config/constants';
import { dateKey, monthKey, netWorthDocId, netWorthTrend, type NetWorthPoint } from '../../../finance';
import { useData, useCoreSynced } from '../../store/data';
import { useBalanceSheet } from '../../store/selectors';

export const useNetWorthHistory = () => {
  // Chỉ chụp khi dữ liệu đã khớp máy chủ — không ghi con số cũ lấy từ bộ nhớ đệm
  const ready = useCoreSynced();
  const hasWallets = useData(s => s.data.sources.length > 0);
  const sheet = useBalanceSheet();
  const [history, setHistory] = useState<NetWorthPoint[] | null>(null);

  useEffect(() => {
    if (!ready || !hasWallets) return;
    let uid: string | undefined;
    try { uid = getAuth().currentUser?.uid; } catch { return; }
    if (!uid) return;
    let cancelled = false;
    (async () => {
      try {
        const db = getFirestore();
        const col = collection(db, 'artifacts', APP_ID, 'users', uid, 'healthMetrics');
        const snap = await getDocs(col);
        const rows = snap.docs.map(d => d.data() as any).filter(d => d.kind === 'networth' && /^\d{4}-\d{2}$/.test(d.month) && typeof d.netWorth === 'number') as NetWorthPoint[];
        if (cancelled) return;
        setHistory(rows);
        const now = new Date();
        const today = dateKey(now);
        if (rows.some(r => r.month === monthKey(now) && r.date === today)) return; // hôm nay đã chụp
        await setDoc(doc(col, netWorthDocId(monthKey(now))), {
          kind: 'networth', month: monthKey(now), date: today, netWorth: sheet.netWorth, liquid: sheet.liquid, longTerm: sheet.longTerm,
          cardDebt: sheet.cardDebt, loanDebt: sheet.loanDebt, savedAt: Timestamp.now()
        });
      } catch { if (!cancelled) setHistory(h => h ?? []); }
    })();
    return () => { cancelled = true; };
    // Chỉ chạy khi dữ liệu lõi sẵn sàng; số liệu ghi là ảnh chụp tại thời điểm đó
  }, [ready, hasWallets]); // eslint-disable-line react-hooks/exhaustive-deps

  const trend = useMemo(() => (history ? netWorthTrend(history, sheet) : null), [history, sheet]);
  return trend;
};
